import {Delete, ErrorOutline, Restore} from '@mui/icons-material';
import {Button, Card, Container, IconButton, Stack, TextField, Typography} from '@mui/material';
import {sum} from 'lodash';
import {useEffect, useState} from 'react';
import {useQuery} from 'react-query';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import ItemBranchName from 'src/components/ItemBranchName';
import SubmitButton from 'src/components/buttons/SubmitButton';
import Counter from 'src/components/inputs/Counter';
import ResponsiveDialog from 'src/components/modals/ResponsiveDiaog';
import InfoTip from 'src/components/ui/InfoTip';
import {default as MuiLink, default as StyledLink} from 'src/components/ui/StyledLink';
import useVisible from 'src/hooks/useVisible';
import {LoadingView} from 'src/routes';
import {getOrder, request} from 'src/utils/AdminApi';
import {devLog} from 'src/utils/devUtils';
import {calcPriceAfterDiscount} from 'src/utils/discountUtils';
import filterDeleted from 'src/utils/filterDeleted';
import {handleRequestError, isNullOrEmpty, uuidv4} from 'src/utils/helperMethods';
import {buildOrderItem} from 'src/utils/orderItemUtils';
import {getImageSrc} from 'src/utils/productUtils';
import {getSiteCurrency} from 'src/utils/selectors';
import ProductsGridSelection from '../products/common/ProductsGridSelection/ProductsGridSelection';
import CartItem from './CartItem.item';
import useOrderItemsSelection from './NewAddOrderPage/useOrderItemsSelection';
import {isFeatureAllowed} from 'src/utils/featureUtils';
import SimpleCartItem from './SimpleCartItem.item';
export default function NewUpdateOrderItemsPage() {
	let {id} = useParams();
	const nav = useNavigate();
	const [orderItems, setItems] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {data: order, isLoading} = useQuery(['order', id], () => getOrder(id), {
		enabled: !!id,
		refetchOnWindowFocus: false,
		refetchOnMount: true,
		onSuccess: (data) => {
			// we need local state to update items after changes are saved
			// we need to filter soft deleted items
			setItems(filterDeleted(data.items));
		},
	});

	const {isVisible, show, hide, toggle, setIsVisible} = useVisible();
	// we use this for new added items
	const {
		handleAddItem,
		handleItemQuantityChange,
		handleRemoveItem,
		addedProducts: newOrderItems = [],
		checkAddedItemExists,
		updateItem,
		resetSelections,
		handleItemChange,
		handleRemoveItemByProductId,
	} = useOrderItemsSelection();
	const allow_simple_products = isFeatureAllowed('simple_products');

	const saveChanges = async (e) => {
		e.preventDefault();

		if (isNullOrEmpty(orderItems.filter((i) => !i.isDeleted)) && isNullOrEmpty(newOrderItems)) {
			toast.error('يجب عليك اضافة منتج واحد على الاقل');
			return;
		}
		setIsSubmitting(true);
		try {
			await request(`/orders/${order.id}/items`, 'PUT', {
				updated_items: orderItems.map((i) => ({
					id: i.id,
					quantity: i.quantity,
					isDeleted: i.isDeleted,
					name: i.name,
					price: i.price,
				})),
				new_items: newOrderItems.map(buildOrderItem),
			});
			toast.success('تم حفظ التعديلات');
			nav(`/orders/${order.uuid}`);
		} catch (err) {
			toast.error(handleRequestError(err));
		} finally {
			setIsSubmitting(false);
		}
	};
	if (isLoading) return <LoadingView />;

	// Inside the component
	const {
		discountExist,
		old_sub_total,
		old_sub_total_with_discount,
		new_sub_total,
		new_sub_total_with_discount,
		sub_total_diff,
		diff_with_discount,
	} = calculatePriceDifferences(order, orderItems, newOrderItems);
	const oldExistingItems = order.items.filter((item) => !item.isDeleted);
	const no_change = isNullOrEmpty(newOrderItems) && itemsAreEqual(orderItems, oldExistingItems);
	const order_canceled_or_returned = order.status === 'cancelled' || order.status === 'returned';

	if (order_canceled_or_returned) {
		return (
			<Container maxWidth="md">
				<Card
					sx={{
						p: 2,

						minHeight: '80vh',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Stack spacing={2} alignItems="center">
						<ErrorOutline sx={{fontSize: 60}} color="error" />
						<Typography variant="h6" color="error">
							{order.status === 'cancelled' ? message.cancelled : message.returned}
						</Typography>
						<StyledLink underline="always" to={`/orders/${order.uuid}`}>
							<Typography variant="subtitle2">الرجوع الي صفحة الطلب</Typography>
						</StyledLink>
					</Stack>
				</Card>
			</Container>
		);
	}

	return (
		<Container maxWidth="md">
			<form onSubmit={saveChanges}>
				<Card sx={{p: 2}}>
					<Stack spacing={2}>
						<div>
							<Stack spacing={1} direction="row" justifyContent="space-between">
								<Typography variant="h3">تعديل الطلب</Typography>
								<InfoTip
									devOnly
									title={`test cases: update item quantity +/-, delete item, add new item \n and make sure invoice created correctly if this is cashier order`}
								/>
							</Stack>
							<StyledLink to={`/orders/${order.uuid}`}>
								<Typography variant="subtitle2">الرجوع للطلب</Typography>
							</StyledLink>
						</div>

						<Stack spacing={1}>
							{orderItems.map((item) => {
								return (
									<Card key={item.id} sx={{p: 1}}>
										<Stack
											alignItems={{xs: 'default', sm: 'center'}}
											direction={{xs: 'column', sm: 'row'}}
											justifyContent="space-between"
										>
											{/* metadata */}
											<Stack spacing={1}>
												<Stack spacing={2} direction="row">
													<img
														loading="lazy"
														alt={item.product?.name}
														width={50}
														height={50}
														src={
															item.product
																? getImageSrc(item.product)
																: item.image || '/static/default-product-image.png'
														}
													/>
													<Stack>
														<Stack spacing={2} direction="row">
															<MuiLink to={item.product ? `/products/${item.product?.id}` : '#'}>
																{item.product ? (
																	<Typography>
																		{item.product?.name ?? item.name}
																		{item.variation?.name && `- ${item.variation?.name}`}
																	</Typography>
																) : (
																	<TextField
																		size="small"
																		label="اسم المنتج"
																		value={item.name}
																		onChange={(e) => {
																			setItems((prevItems) =>
																				prevItems.map((orderItem) =>
																					orderItem.id === item.id ? {...orderItem, name: e.target.value} : orderItem,
																				),
																			);
																		}}
																	/>
																)}
															</MuiLink>

															{!item.product && (
																<TextField
																	size="small"
																	label="السعر"
																	value={item.price}
																	onChange={(e) => {
																		setItems((prevItems) =>
																			prevItems.map((orderItem) =>
																				orderItem.id === item.id ? {...orderItem, price: e.target.value} : orderItem,
																			),
																		);
																	}}
																/>
															)}
														</Stack>

														{item.product && (
															<Stack spacing={3} direction="row" justifyContent="space-between">
																<Typography>
																	{calcPriceAfterDiscount(item.price, item.discount)} {getSiteCurrency()}
																</Typography>
																<ItemBranchName item={item} />
															</Stack>
														)}
													</Stack>
												</Stack>
											</Stack>
											{/* actions */}
											<Stack justifyContent="space-between" alignItems="center" spacing={1} direction="row">
												<Counter
													defaultValue={item.quantity}
													onChange={(newValue) => {
														setItems((prevItems) =>
															prevItems.map((orderItem) =>
																orderItem.id === item.id ? {...orderItem, quantity: newValue} : orderItem,
															),
														);
													}}
													min={1}
												/>
												<IconButton
													color={item.isDeleted ? 'grey' : 'error'}
													onClick={async () => {
														setItems((prevItems) =>
															prevItems.map((orderItem) =>
																orderItem.id === item.id
																	? {...orderItem, isDeleted: !orderItem.isDeleted}
																	: orderItem,
															),
														);
													}}
												>
													{item.isDeleted ? <Restore /> : <Delete />}
												</IconButton>
											</Stack>
										</Stack>
									</Card>
								);
							})}
						</Stack>

						<div>
							<Stack mt={2} spacing={1} direction="row" justifyContent="space-between">
								<Typography variant="h6">اضافة منتجات اخرى</Typography>
								<Button onClick={show}>اختر</Button>
							</Stack>
							<ResponsiveDialog
								maxWidth="md"
								open={isVisible}
								setOpen={setIsVisible}
								actions={
									<Button sx={{py: 1.1}} fullWidth onClick={() => hide()} variant="contained">
										تأكيد
									</Button>
								}
							>
								<ProductsGridSelection
									columns={{xl: 3}}
									checkAddedItemExists={checkAddedItemExists}
									handleAddItem={handleAddItem}
									handleRemoveItem={handleRemoveItem}
									handleRemoveItemByProductId={handleRemoveItemByProductId}
								/>
							</ResponsiveDialog>

							<Stack my={1} spacing={1}>
								<Stack mt={2} spacing={2}>
									{newOrderItems.map((item) => {
										if (item.simple) {
											return (
												<SimpleCartItem
													handleItemChange={(id, changes) => updateItem(id, changes)}
													handleItemQuantityChange={handleItemQuantityChange}
													handleRemoveItem={handleRemoveItem}
													item={item}
													key={item.id}
												/>
											);
										}
										return (
											<Stack key={item.id}>
												<CartItem
													handleItemQuantityChange={handleItemQuantityChange}
													handleRemoveItem={handleRemoveItem}
													item={item}
													handleItemChange={handleItemChange}
													showBranch={!order.is_cashier}
												/>
												{item.error && (
													<Typography variant="body2" color="error">
														يوجد مشكلة في اضافة المنتج
													</Typography>
												)}
											</Stack>
										);
									})}
								</Stack>

								{allow_simple_products && (
									<Button
										type="button"
										color="grey"
										onClick={() => {
											handleAddItem({
												simple: true,
												price: '',
												name: ``,
												// custom id so we can select/deselect item
												id: uuidv4(),
												isVariation: false,
												selectedQuantity: 1,
											});
										}}
									>
										إضافة منتج بسيط
									</Button>
								)}
							</Stack>
						</div>
					</Stack>
				</Card>
				{/* {!isNullOrEmpty(itemsToRemove) && !isNullOrEmpty(newOrderItems) && ( */}
				<Card sx={{p: 2, mt: 2, opacity: no_change ? 0 : 1}}>
					<Typography>القيمة السابقة: {old_sub_total}</Typography>
					<Typography>القيمة الجديدة: {new_sub_total}</Typography>
					{discountExist && <Typography mt={2}>قيمة التخفيض: {order.discount}</Typography>}
					{discountExist && (
						<Typography mt={2}>القيمة السابقة بعد التخفيض: {old_sub_total_with_discount}</Typography>
					)}
					{discountExist && (
						<Typography>القيمة الجديدة بعد التخفيض: {new_sub_total_with_discount}</Typography>
					)}
					<Typography mt={2} fontWeight="bold">
						الفرق: {diff_with_discount > 0 ? `+${diff_with_discount}` : diff_with_discount}
					</Typography>

					<SubmitButton disabled={no_change} sx={{mt: 3}} isSubmitting={isSubmitting}>
						حفظ التغيرات
					</SubmitButton>
				</Card>
				{/* )} */}
			</form>
		</Container>
	);
}
const message = {
	cancelled: 'لا يمكن تعديل طلب ملغي',
	returned: 'لا يمكن تعديل الطلب بعد ارجاعه',
};
function calculatePriceDifferences(order, orderItems, newOrderItems) {
	const old_sub_total = order.subTotal;
	const old_sub_total_with_discount = old_sub_total - order.discount;

	const new_sub_total =
		sum(
			orderItems
				.filter((item) => !item.isDeleted)
				.map((item) => calcPriceAfterDiscount(item.price, item.discount) * item.quantity),
		) +
		sum(
			newOrderItems.map(
				(item) => calcPriceAfterDiscount(item.price, item.discount) * item.selectedQuantity,
			),
		);

	const new_sub_total_with_discount = new_sub_total - order.discount;

	const sub_total_diff = new_sub_total - old_sub_total;
	const diff_with_discount = new_sub_total_with_discount - old_sub_total_with_discount;

	return {
		discountExist: order.discount > 0,
		old_sub_total,
		old_sub_total_with_discount,
		new_sub_total,
		new_sub_total_with_discount,
		sub_total_diff,
		diff_with_discount,
	};
}

function itemsAreEqual(items1, items2) {
	if (items1.length !== items2.length) {
		devLog('itemsAreEqual', 'length not equal', items1.length, items2.length);

		return false;
	}

	for (let i in items1) {
		const item1 = items1[i];
		const item2 = items2[i];

		if (item1.id !== item2.id) {
			devLog('itemsAreEqual', 'id not equal', item1.id, item2.id);
			return false;
		}

		if (item1.isDeleted !== item2.isDeleted) {
			devLog('itemsAreEqual', 'isDeleted not equal', item1.isDeleted, item2.isDeleted);
			return false;
		}

		if (item1.quantity !== item2.quantity) {
			devLog('itemsAreEqual', 'quantity not equal', item1.quantity, item2.quantity);
			return false;
		}

		if (item1.name !== item2.name) {
			devLog('itemsAreEqual', 'name not equal', item1.name, item2.name);
			return false;
		}

		if (item1.price !== item2.price) {
			devLog('itemsAreEqual', 'price not equal', item1.price, item2.price);
			return false;
		}
	}

	devLog('itemsAreEqual', 'items are equal');
	return true;
}
